/**
 * BB-14d1/BB-14d2c2 -- the React boundary for the browser media leg.
 *
 * Deliberately thin on SIP: the hook owns *no* SIP logic. It holds a
 * `MediaLegClient`, mirrors its non-secret snapshot into state, drives
 * the ordering, and exposes intents. Every SIP type stays behind
 * `mediaLegClient`, so a component can never touch a `UserAgent`, a
 * session, or the secret.
 *
 * **The ordering this hook owns:**
 *
 *   microphone -> bootstrap -> UserAgent -> WSS -> REGISTER
 *     -> INVITE conference -> Established -> poll /readiness
 *     -> ready:true -> Product Ready
 *
 * The microphone comes first and that is not cosmetic: bootstrap mints
 * an identity under `one_active_identity_per_session`, so acquiring it
 * for a tab whose microphone is denied would leave an ACTIVE identity
 * blocking the agent's own retry with `already_active`.
 *
 * **Established is never enough.** Product Ready needs the backend's
 * own `GET /agent-media/readiness` to answer `ready: true`. The browser
 * knows only that its INVITE was answered; whether the resulting
 * channel actually appeared in the agent's assigned conference is a
 * server-side observation, and it is the only one that counts.
 *
 * **Strict Mode.** React double-invokes effects in development, and a
 * naive implementation would issue two bootstraps, build two
 * UserAgents, send two REGISTERs, and let the first controller's
 * cleanup revoke the second controller's identity. Three guards
 * prevent that, described at their definitions below: a run token, an
 * in-flight promise, and a per-controller revoke that names its own
 * exact peer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  bootstrapAgentMedia,
  fetchAgentMediaReadiness,
  revokeAgentMedia,
} from '../telephony/agentMediaIdentityApi';
import { MediaLegClient } from '../telephony/mediaLegClient';
import { MediaLegState, isMediaHealthy } from '../telephony/mediaLegStates';
import { acquireMicrophone, releaseMicrophone } from '../telephony/microphone';
import {
  AudioLifecycleState,
  CONNECTING_AUDIO_TIMEOUT_MS,
  READINESS_POLL_MS,
  ReadinessReason,
  deriveAudioLifecycleState,
  establishmentTimeoutApplies,
  requiresLocalTeardown,
  startDialingAvailable,
} from '../telephony/audioLifecycle';

const INITIAL_SNAPSHOT = Object.freeze({
  state: MediaLegState.DISCONNECTED,
  reason: null,
  peerName: null,
  expiresAt: null,
  leaseGeneration: null,
  sipRealm: null,
  mediaWssUrl: null,
  conferenceDialPrefix: null,
  confExten: null,
  conferenceTarget: null,
  sessionEstablished: false,
  playbackBlocked: false,
  hasUserAgent: false,
});

export function useAgentMediaLeg({ clientFactory = null, microphone = acquireMicrophone } = {}) {
  const [snapshot, setSnapshot] = useState(INITIAL_SNAPSHOT);
  const [readiness, setReadiness] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [failure, setFailure] = useState(null);
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * BB-14e -- "this identity's leg has been confirmed healthy at least
   * once".
   *
   * The browser SIP leg is the agent's phone: it is meant to persist for
   * the whole campaign-login session and across every RATIO call. Before
   * this latch existed, `deriveAudioLifecycleState` was memoryless, so a
   * single post-Ready `ready:false` -- which a customer call ending
   * routinely produces -- put an established leg back into
   * CONNECTING_AUDIO, re-armed the *setup* timeout, and revoked a working
   * phone 45 seconds later with no user action. Production did exactly
   * that after a DROP/AMD call.
   *
   * Scoped to the current identity by construction: every path that
   * replaces or tears down the identity clears it (see `_resetForNewRun`
   * and the teardown sites), so a genuinely new leg gets a genuinely new
   * establishment timeout.
   */
  const [hasBeenReady, setHasBeenReady] = useState(false);

  const clientRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Strict Mode guard 1 -- the run token.
   *
   * Every `connectAudio()` takes the next token. An asynchronous step
   * that finishes after a newer run started sees a stale token and
   * commits nothing: no state, no transition, and no further steps. A
   * double-invoked effect therefore produces one live lifecycle even
   * though two were requested.
   */
  const runRef = useRef(0);

  /**
   * Strict Mode guard 2 -- the in-flight promise.
   *
   * A second concurrent `connectAudio()` returns the first one's
   * promise instead of starting a second lifecycle, so the duplicate
   * never reaches `getUserMedia` or `POST /bootstrap` at all. This is
   * the guard that prevents a duplicate identity being minted, rather
   * than merely being ignored after the fact.
   */
  const inFlightRef = useRef(null);

  const applySnapshot = useCallback((next) => {
    // A SIP callback can fire after unmount; setting state then is a
    // React warning at best and a leak at worst.
    if (mountedRef.current) {
      setSnapshot(next);
    }
  }, []);

  const makeClient = useCallback(() => {
    const onStateChange = (next) => applySnapshot(next);
    return clientFactory
      ? clientFactory({ onStateChange })
      : new MediaLegClient({ onStateChange });
  }, [applySnapshot, clientFactory]);

  /**
   * Strict Mode guard 3 -- teardown names its own peer.
   *
   * The client being torn down is captured here, and its peer is read
   * from that instance. A controller that has already been replaced
   * therefore revokes the identity *it* was issued and can never revoke
   * its replacement's -- the server-side half of the same property is
   * BB-14d2c1b's exact-peer scoped revoke, which refuses to match
   * anything but this account, connection, agent and peer.
   *
   * Revoke is best-effort and convergent: a failure resolves `false`
   * and is dropped. A failed cleanup must never drive a native state
   * transition, and BB-6L campaign logout remains the authoritative
   * server-side cleanup regardless.
   */
  const disposeClient = useCallback((client, reason) => {
    if (!client) return;
    const peerName = typeof client.getPeerName === 'function' ? client.getPeerName() : null;
    client.teardown(reason);
    if (peerName) {
      revokeAgentMedia(peerName);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Invalidate any run in flight so its later steps commit nothing.
      runRef.current += 1;
      inFlightRef.current = null;
      const client = clientRef.current;
      clientRef.current = null;
      disposeClient(client, 'unmounted');
    };
  }, [disposeClient]);

  /**
   * Run the whole setup. Returns `null` on success or a bounded failure
   * code, and never throws.
   */
  const connectAudio = useCallback(async () => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const run = async () => {
      runRef.current += 1;
      const token = runRef.current;
      const current = () => runRef.current === token && mountedRef.current;

      setBusy(true);
      setStarted(true);
      setFailure(null);
      setRefusal(null);
      setReadiness(null);
      // BB-14e: a new run mints a new identity, so it earns a fresh
      // pre-Ready establishment window. Covers Retry Audio too, which
      // disposes the old leg and then calls straight through to here.
      setHasBeenReady(false);

      // 1. Microphone, before anything is minted server-side.
      const { stream, failure: micFailure } = await microphone();
      if (!current()) {
        releaseMicrophone(stream);
        return micFailure || 'superseded';
      }
      if (micFailure) {
        setFailure(micFailure);
        return micFailure;
      }

      // 2. Identity. Only now, with a working microphone in hand.
      let payload;
      try {
        payload = await bootstrapAgentMedia();
      } catch (caught) {
        // Only the message is retained -- never the axios error, whose
        // `config` carries the Authorization header.
        releaseMicrophone(stream);
        if (current()) setFailure('bootstrap_failed');
        return 'bootstrap_failed';
      }
      if (!current()) {
        releaseMicrophone(stream);
        return 'superseded';
      }
      if (!payload || !payload.issued) {
        releaseMicrophone(stream);
        const code = (payload && payload.refusal) || 'unknown';
        setRefusal(code);
        setFailure(code);
        return code;
      }

      // 3. Build the controller and open the leg. Every failure past
      // this point must revoke: an identity is now ACTIVE.
      const client = makeClient();
      clientRef.current = client;
      try {
        await client.prepare(payload);
        if (!current()) throw new Error('superseded');

        await client.connect({ microphoneStream: stream });
        if (!current()) throw new Error('superseded');
        if (client.getSnapshot().state === MediaLegState.FAILED) {
          throw new Error('registration_failed');
        }

        await client.joinConference();
        if (!current()) throw new Error('superseded');
        if (client.getSnapshot().state === MediaLegState.FAILED) {
          throw new Error('invite_failed');
        }
      } catch (caught) {
        const code = caught instanceof Error ? caught.message : 'audio_setup_failed';
        // Setup failed after bootstrap: release the exact identity this
        // controller was issued rather than leaving it ACTIVE to block
        // the agent's own retry with `already_active`.
        disposeClient(client, code);
        if (clientRef.current === client) {
          clientRef.current = null;
        }
        if (current()) setFailure(code === 'superseded' ? null : code);
        return code;
      }
      return null;
    };

    const promise = run().finally(() => {
      inFlightRef.current = null;
      if (mountedRef.current) {
        setBusy(false);
      }
    });
    inFlightRef.current = promise;
    return promise;
  }, [disposeClient, makeClient, microphone]);

  /**
   * Poll the authoritative readiness answer.
   *
   * One interval, in the authorized 3-5s band, and only while a session
   * exists. This is deliberately the only loop this hook adds: the
   * Production shell already polls call state, and the S2 runtime
   * already beats server-side.
   */
  useEffect(() => {
    if (!started) return undefined;

    let cancelled = false;
    const poll = async () => {
      let answer;
      try {
        answer = await fetchAgentMediaReadiness();
      } catch (error) {
        // A transient network failure is not evidence of media loss.
        // Hold the last answer and try again on the next tick.
        return;
      }
      if (cancelled || !mountedRef.current) return;
      setReadiness(answer);

      const client = clientRef.current;
      if (client) {
        client.applyReadiness(answer);
      }
      if (requiresLocalTeardown(answer.reason)) {
        // Another tab holds the session. Tear this controller's own
        // media down and stop; it must not keep a leg against a
        // superseded identity.
        clientRef.current = null;
        disposeClient(client, ReadinessReason.LEASE_SUPERSEDED);
        setFailure(ReadinessReason.LEASE_SUPERSEDED);
        setHasBeenReady(false);
      }
    };

    poll();
    const timer = setInterval(poll, READINESS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [started, disposeClient]);

  const audioState = deriveAudioLifecycleState({
    started,
    sessionEstablished: snapshot.sessionEstablished,
    readiness,
    failed: failure !== null,
    hasBeenReady,
  });

  // Set the latch the moment READY is first derived for this identity.
  // Deriving first and latching after keeps the rule in one place --
  // `deriveAudioLifecycleState` stays the single definition of READY.
  useEffect(() => {
    if (audioState === AudioLifecycleState.READY && !hasBeenReady) {
      setHasBeenReady(true);
    }
  }, [audioState, hasBeenReady]);

  /**
   * Bounded CONNECTING_AUDIO timeout.
   *
   * Derived from the S2 Logged In cadence and the observation freshness
   * contract -- see `CONNECTING_AUDIO_TIMEOUT_MS`. Without it a leg
   * that registered but never appeared in the conference would leave
   * the agent watching "Connecting Audio…" indefinitely while Start
   * Dialing stayed unavailable and nothing explained why.
   */
  useEffect(() => {
    // BB-14e: CONNECTING_AUDIO only. Once the latch is set the state is
    // DEGRADED instead, which this timer deliberately does not match --
    // an established leg is never torn down for staying unconfirmed.
    if (!establishmentTimeoutApplies(audioState)) return undefined;
    const timer = setTimeout(() => {
      if (!mountedRef.current) return;
      const client = clientRef.current;
      clientRef.current = null;
      disposeClient(client, 'audio_timeout');
      setFailure('audio_timeout');
    }, CONNECTING_AUDIO_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [audioState, disposeClient]);

  /** Re-attempt remote playback from a user gesture. Touches no SIP state. */
  const enableAudio = useCallback(async () => {
    const client = clientRef.current;
    if (!client || typeof client.enablePlayback !== 'function') return false;
    return client.enablePlayback();
  }, []);

  /**
   * Retry after a failure. Disposes the previous controller -- naming
   * its own peer -- before starting a new lifecycle, so the retry can
   * never be refused with `already_active` by an identity this browser
   * abandoned.
   */
  const retryAudio = useCallback(async () => {
    const client = clientRef.current;
    clientRef.current = null;
    disposeClient(client, 'retry');
    setFailure(null);
    setRefusal(null);
    setReadiness(null);
    return connectAudio();
  }, [connectAudio, disposeClient]);

  /** Campaign teardown. BB-6L remains the authoritative server cleanup. */
  const stopAudio = useCallback(() => {
    runRef.current += 1;
    const client = clientRef.current;
    clientRef.current = null;
    disposeClient(client, 'campaign_ended');
    setStarted(false);
    setReadiness(null);
    setFailure(null);
    setRefusal(null);
    setHasBeenReady(false);
    setSnapshot(INITIAL_SNAPSHOT);
  }, [disposeClient]);

  const observeLeaseGeneration = useCallback((generation) => {
    const client = clientRef.current;
    return client ? client.observeLeaseGeneration(generation) : null;
  }, []);

  const teardown = useCallback(
    (reason) => {
      const client = clientRef.current;
      clientRef.current = null;
      disposeClient(client, reason);
      setHasBeenReady(false);
      return null;
    },
    [disposeClient],
  );

  return {
    state: snapshot.state,
    snapshot,
    readiness,
    refusal,
    failure,
    busy,
    audioState,
    hasBeenReady,
    startDialingAvailable: startDialingAvailable(audioState),
    playbackBlocked: snapshot.playbackBlocked === true,
    isMediaHealthy: isMediaHealthy(snapshot.state),
    connectAudio,
    enableAudio,
    retryAudio,
    stopAudio,
    observeLeaseGeneration,
    teardown,
  };
}

export default useAgentMediaLeg;
